import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import EmblaCarousel, { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel';
import Autoplay, { type AutoplayType } from 'embla-carousel-autoplay';
import { SELECTORS } from '../../constants/selectors';
import { EVENT_NAMES, type CarouselSlideChangedDetail } from '../../constants/events';
import { emitEvent } from '../../utils/dom';

const ACTIVE_CLASS = 'is-active';
const PLAYING_CLASS = 'is-playing';

/**
 * 汎用カルーセルコンポーネント
 *
 * @element kd-carousel
 * @description Embla Carousel ベースの汎用カルーセル。
 *              HTML は Liquid 側で持ち、Lit は振る舞い（ドラッグ、ナビ、ドット）のみ担当する。
 *
 * @attr {boolean} data-loop - ループ有効
 * @attr {"start"|"center"|"end"} data-align - スライドの揃え位置
 * @attr {boolean} data-drag-free - フリードラッグ有効
 * @attr {string} data-slides-to-scroll - スクロール枚数
 * @attr {boolean} data-autoplay - 自動再生有効
 * @attr {string} data-autoplay-delay - 自動再生間隔（ms）デフォルト 4000
 *
 * @fires kd-carousel-slide-changed スライドが切り替わったとき
 */
@customElement('kd-carousel')
export class KdCarousel extends LitElement {
  private emblaApi: EmblaCarouselType | null = null;

  private abortController: AbortController | null = null;

  createRenderRoot() {
    return this;
  }

  render() {
    return nothing;
  }

  firstUpdated(): void {
    const viewport = this.querySelector<HTMLElement>(SELECTORS.CAROUSEL.VIEWPORT);
    if (!viewport) return;

    this.abortController = new AbortController();
    const options = this.buildOptions();
    const plugins = this.buildPlugins();
    this.emblaApi = EmblaCarousel(viewport, options, plugins);

    this.setupNavButtons();
    this.setupDots();
    this.setupAutoplayToggle();
    this.setupEventListeners();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.abortController?.abort();
    this.abortController = null;
    this.emblaApi?.destroy();
    this.emblaApi = null;
  }

  /** data-* 属性から Embla オプションを組み立てる */
  private buildOptions(): EmblaOptionsType {
    const options: EmblaOptionsType = {};

    if (this.hasAttribute('data-loop')) {
      options.loop = true;
    }

    const align = this.getAttribute('data-align');
    if (align === 'start' || align === 'center' || align === 'end') {
      options.align = align;
    }

    if (this.hasAttribute('data-drag-free')) {
      options.dragFree = true;
    }

    const slidesToScroll = this.getAttribute('data-slides-to-scroll');
    if (slidesToScroll) {
      const num = parseInt(slidesToScroll, 10);
      if (!Number.isNaN(num) && num > 0) {
        options.slidesToScroll = num;
      }
    }

    return options;
  }

  /** data-autoplay 属性がある場合に Autoplay プラグインを返す */
  private buildPlugins(): ReturnType<typeof Autoplay>[] {
    if (!this.hasAttribute('data-autoplay')) return [];

    const delayAttr = this.getAttribute('data-autoplay-delay');
    const delay = delayAttr ? parseInt(delayAttr, 10) : 4000;

    return [
      Autoplay({
        delay: Number.isNaN(delay) ? 4000 : delay,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      }),
    ];
  }

  /** Prev/Next ボタンのクリックイベントを設定（複数配置可: SP/PC で別位置） */
  private setupNavButtons(): void {
    const signal = this.abortController!.signal;
    this.querySelectorAll<HTMLButtonElement>(SELECTORS.CAROUSEL.PREV).forEach((btn) => {
      btn.addEventListener('click', () => this.emblaApi?.scrollPrev(), { signal });
    });
    this.querySelectorAll<HTMLButtonElement>(SELECTORS.CAROUSEL.NEXT).forEach((btn) => {
      btn.addEventListener('click', () => this.emblaApi?.scrollNext(), { signal });
    });
  }

  /** ドットナビゲーションのクリック���ベントを設定 */
  private setupDots(): void {
    const signal = this.abortController!.signal;
    const dots = this.querySelectorAll<HTMLElement>(SELECTORS.CAROUSEL.DOT);
    const snapCount = this.emblaApi!.scrollSnapList().length;

    if (dots.length > 0 && dots.length !== snapCount) {
      console.warn(
        `[kd-carousel] ドット数(${dots.length})とスクロールスナップ数(${snapCount})が一致しません`,
      );
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.emblaApi?.scrollTo(index), { signal });
    });
  }

  /** 自動再生トグルボタンのクリックイベントを設定 */
  private setupAutoplayToggle(): void {
    const toggleBtn = this.querySelector<HTMLButtonElement>(
      SELECTORS.CAROUSEL.AUTOPLAY_TOGGLE,
    );
    if (!toggleBtn || !this.emblaApi) return;

    const autoplay = this.emblaApi.plugins()?.autoplay as AutoplayType | undefined;
    if (!autoplay) return;

    const signal = this.abortController!.signal;

    // 初期状態: 再生中
    toggleBtn.classList.add(PLAYING_CLASS);

    toggleBtn.addEventListener(
      'click',
      () => {
        if (autoplay.isPlaying()) {
          autoplay.stop();
          toggleBtn.classList.remove(PLAYING_CLASS);
        } else {
          autoplay.play();
          toggleBtn.classList.add(PLAYING_CLASS);
        }
      },
      { signal },
    );
  }

  /** Embla イベントリスナーを設定 */
  private setupEventListeners(): void {
    if (!this.emblaApi) return;

    const onSelect = (): void => {
      this.syncVideos();
      this.updateNavButtons();
      this.updateDots();
      this.updateCounter();
      this.emitSlideChanged();
    };

    this.emblaApi.on('select', onSelect);
    this.emblaApi.on('init', onSelect);
  }

  /**
   * スライド切り替え時の動画制御。表示中スライドが `data-carousel-autoplay` を持てばその動画を再生し、
   * それ以外の動画は止める（画面外で再生・音が続かないように）。
   * 1 スライド = 1 スナップ（v2-product-image-slider）前提で、スナップ番号をスライド番号として扱う。
   */
  private syncVideos(): void {
    if (!this.emblaApi) return;
    const selected = this.emblaApi.selectedScrollSnap();
    this.querySelectorAll<HTMLElement>(SELECTORS.CAROUSEL.SLIDE).forEach((slide, i) => {
      const play = i === selected && slide.hasAttribute('data-carousel-autoplay');
      slide.querySelectorAll('video').forEach((v) => {
        if (play) {
          // 自動再生がブラウザに拒否されても（省電力モード等）コントロールから再生できるので握りつぶす
          v.play()?.catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    });
  }

  /** Prev/Next ボタンの disabled 状態を更新（複数配置対応） */
  private updateNavButtons(): void {
    if (!this.emblaApi) return;

    const canPrev = this.emblaApi.canScrollPrev();
    const canNext = this.emblaApi.canScrollNext();

    this.querySelectorAll<HTMLButtonElement>(SELECTORS.CAROUSEL.PREV).forEach((btn) => {
      btn.disabled = !canPrev;
    });
    this.querySelectorAll<HTMLButtonElement>(SELECTORS.CAROUSEL.NEXT).forEach((btn) => {
      btn.disabled = !canNext;
    });
  }

  /**
   * スライド番号カウンタを更新する。
   * 0 埋め幅は `data-counter-pad` 属性で指定（例: total=20 → "2"）。
   * 属性が無い場合は初期 textContent の文字数を幅として使う（後方互換）。
   */
  private updateCounter(): void {
    if (!this.emblaApi) return;
    const current = this.emblaApi.selectedScrollSnap() + 1;
    const padAttr = parseInt(this.dataset.counterPad ?? '', 10);
    const padFromAttr = Number.isNaN(padAttr) ? 0 : padAttr;
    this.querySelectorAll<HTMLElement>(SELECTORS.CAROUSEL.COUNTER_CURRENT).forEach((el) => {
      const width =
        padFromAttr || (el.textContent ?? '').trim().length || String(current).length;
      el.textContent = String(current).padStart(width, '0');
    });
  }

  /** ドットの active / aria-current 状態を更新 */
  private updateDots(): void {
    if (!this.emblaApi) return;

    const selectedIndex = this.emblaApi.selectedScrollSnap();
    const dots = this.querySelectorAll<HTMLElement>(SELECTORS.CAROUSEL.DOT);

    dots.forEach((dot, index) => {
      const isActive = index === selectedIndex;
      dot.classList.toggle(ACTIVE_CLASS, isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /** スライド変更イベントを発火 */
  private emitSlideChanged(): void {
    if (!this.emblaApi) return;

    const detail: CarouselSlideChangedDetail = {
      index: this.emblaApi.selectedScrollSnap(),
      scrollSnapCount: this.emblaApi.scrollSnapList().length,
    };
    emitEvent(this, EVENT_NAMES.CAROUSEL.SLIDE_CHANGED, detail);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-carousel': KdCarousel;
  }
}
